import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/auth";
import { ApiError, loginUnified, requestPasswordResetUnified, useBrandName } from "@/lib/api";
import { ShieldIcon } from "@/components/Icons";

interface LocationState {
  from?: { pathname: string };
}

type Mode = "login" | "forgot";

export default function LoginPage() {
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const brandName = useBrandName();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotSent, setForgotSent] = useState(false);

  if (user) {
    const from = (location.state as LocationState | undefined)?.from?.pathname ?? "/";
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // Phase 23 unified login: backend resolves super-admin vs tenant
      // operator and tells us where to send the browser. Hard-redirect via
      // window.location.href so an operator login lands on the operator
      // SPA bundle at /t/<slug>/ui/ (we're currently inside the admin SPA
      // bundle — React Router can't navigate across bundles).
      const result = await loginUnified(email, password);
      window.location.href = result.redirect;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(t("login.errorInvalidCredentials"));
      } else {
        console.error("login failed", err);
        setError(t("login.errorGeneric"));
      }
      setSubmitting(false);
    }
    // No `finally setSubmitting(false)` on success — page is about to
    // reload, leaving the button disabled prevents double-submits.
  }

  async function handleForgot(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await requestPasswordResetUnified(email);
      // Endpoint always returns 200 — don't leak whether the email
      // matched a real user.
      setForgotSent(true);
    } catch (err) {
      console.error("password reset request failed", err);
      setError(t("login.forgotError"));
    } finally {
      setSubmitting(false);
    }
  }

  if (mode === "forgot") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <form
          onSubmit={handleForgot}
          className="w-full max-w-sm space-y-6 rounded-lg border border-border bg-card p-8 shadow-sm"
        >
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-accent/15 ring-1 ring-accent/25 text-accent">
              <ShieldIcon size={18} />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t("login.forgotTitle")}
              </p>
              <h1 className="text-lg font-semibold tracking-tight">{brandName || "Admin"}</h1>
            </div>
          </div>
          {forgotSent ? (
            <p className="rounded-md border border-border bg-muted/20 px-3 py-2 text-sm">
              {t("login.forgotSuccess")}
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{t("login.forgotSubtitle")}</p>
              <label className="block">
                <span className="text-sm font-medium">{t("login.emailLabel")}</span>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
              {error && (
                <p
                  role="alert"
                  className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition disabled:opacity-50"
              >
                {submitting ? t("login.forgotSubmitting") : t("login.forgotSubmit")}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => {
              setMode("login");
              setForgotSent(false);
              setError(null);
            }}
            className="block w-full text-center text-xs text-muted-foreground hover:text-foreground"
          >
            ← {t("login.forgotBack")}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-6 rounded-lg border border-border bg-card p-8 shadow-sm"
      >
        <div className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-md bg-accent/15 ring-1 ring-accent/25 text-accent">
            <ShieldIcon size={18} />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t("login.title")}
            </p>
            <h1 className="text-lg font-semibold tracking-tight">
              {brandName ? `${brandName} · admin` : "Admin"}
            </h1>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{t("login.subtitle")}</p>
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium">{t("login.emailLabel")}</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">{t("login.passwordLabel")}</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
        </div>
        {error && (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition disabled:opacity-50"
        >
          {submitting ? t("login.submitting") : t("login.submit")}
        </button>
        <button
          type="button"
          onClick={() => {
            setMode("forgot");
            setPassword("");
            setError(null);
          }}
          className="block w-full text-center text-xs text-muted-foreground hover:text-foreground"
        >
          {t("login.forgotPassword")}
        </button>
      </form>
    </div>
  );
}
